import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { PaperClip, User } from 'components';
import { Document2Icon, SendIcon, ImageIcon } from 'assets/icons';
import { RevisionService, StorageService } from 'services';
import { Constants, Globals, SocketEvents } from 'utils';
import moment from 'moment';
import * as Jetemit from 'jetemit';
import FileUploadPlaceholder from './file-upload-placeholder';
import { ImagesAndDocuments } from './paperclip';

const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tiff',
  'tif',
  'avif',
  'heic',
  'heif',
];

interface Props extends RootState {
  loadComments: (withoutLoading?: boolean) => Promise<any>;
  sendComments: (item: any) => Promise<any>;
  currentUser: any;
  writeComments: boolean;
  // Id del registro (revision_id o preparation_id) y del servicio
  // (Constants.SERVICES.REVISION / PREPARATION), para filtrar el evento de
  // socket COMMENTS.MESSAGE y saber si le corresponde a este modal.
  itemId?: number;
  service?: number;
}

class ModalComments extends React.Component<Props> {
  messagesEnd: any = null;
  textareaRef: any = null;
  timer: any = undefined;

  state = {
    messages: [],
    form: {
      message: '',
    },
    textareaHeight: 55.8,
    chat: null,
    visible: false,
    uploading: false,
    uploading_is_image: false,
    unsubscriber: () => {},
  };

  componentDidMount() {
    this.load();

    // Refresco en tiempo real
    this.setState({
      unsubscriber: Jetemit.on(SocketEvents.COMMENTS.MESSAGE, (data: any) => {
        if (data.service != this.props.service || data.item_id != this.props.itemId) return;
        if (!data.comment) return;

        const incoming = data.comment;
        const messages: any = [...this.state.messages];

        if (incoming.user_id === this.props.user?.id) {
          if (messages.some((m: any) => m.id === incoming.id)) return;
          messages.push(incoming);
        } else if (!messages.some((m: any) => m.id === incoming.id)) {
          messages.push(incoming);
        }

        this.setState({ messages }, () => this.scrollToBottom());
      }),
    });
  }

  componentWillUnmount() {
    this.state.unsubscriber();
  }

  load = async (withoutLoading: boolean = false) => {
    const res: any = await this.props.loadComments(withoutLoading);
    this.setState(
      {
        messages: res.messages || [],
      },
      () => {
        this.scrollToBottom();
      },
    );
  };

  change = (e: any) => {
    this.setState({
      form: {
        ...this.state.form,
        [e.target.name]: e.target.value,
      },
    });
  };

  scrollToBottom = (behavior: ScrollBehavior | 'instant' = 'smooth') => {
    this.messagesEnd.scrollTo({
      behavior,
      top: this.messagesEnd.scrollHeight,
    });
  };

  autoGrow = (e: any) => {
    if (e.nativeEvent.keyCode == 13) {
      e.preventDefault();
      this.send();
    }
    this.textareaRef.style.height = 'inherit';
    this.textareaRef.style.height = `${Math.max(this.textareaRef.scrollHeight, 55.8)}px`;
  };

  send = async (file = '') => {
    if (this.state.form.message || file != '') {
      const message = this.state.form.message;
      this.setState(
        {
          form: { ...this.state.form, message: '' },
          height: 0,
          uploading: file != '',
        },
        () => {
          if (file != '') {
            setTimeout(() => this.scrollToBottom('instant'), 0);
          }
        },
      );
      await this.props.sendComments({ message, file });
      this.setState({ uploading: false });
      await this.load(true);
    }
  };

  openFile = async (key: string) => {
    const opened = await StorageService.openFile(key);
    if (!opened) Globals.showError('No se pudo obtener el archivo');
  };

  getFileLabel = (filePath: string) => {
    const ext = filePath.split('.').pop()?.toLowerCase() || '';
    if (IMAGE_EXTENSIONS.includes(ext)) return 'IMAGEN';
    return ext.toUpperCase();
  };

  render() {
    const { messages, uploading } = this.state;
    const { currentUser } = this.props;

    return (
      <div id='modal-comments'>
        <div className='row'>
          <div className='col-md-12'>
            <div className='container-header'>
              <User image={currentUser?.photo} name={currentUser?.full_name} />
            </div>

            <div
              className='container-messages'
              ref={(el) => {
                this.messagesEnd = el;
              }}
            >
              {!!messages.length ? (
                messages.map((item: any) => {
                  if (item.file) {
                    return (
                      <button
                        key={item.id}
                        type='button'
                        className={`link-file ${item.user_id == this.props.user?.id ? 'self' : ''}`}
                        onClick={() => this.openFile(item.file)}
                      >
                        <img
                          src={
                            IMAGE_EXTENSIONS.includes(item.file.split('.').pop()?.toLowerCase())
                              ? ImageIcon
                              : Document2Icon
                          }
                        />
                        <p>
                          Archivo Adjunto <span>({this.getFileLabel(item.file)})</span>
                        </p>
                        <p className='date'>{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</p>
                      </button>
                    );
                  } else {
                    return (
                      <div
                        key={item.id}
                        className={`item-message ${item.user_id == this.props.user?.id ? 'self' : ''}`}
                      >
                        <p className='message'>{item.message}</p>
                        <p className='date'>{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</p>
                      </div>
                    );
                  }
                })
              ) : (
                <p className='no-chat'>No se han hecho comentarios para esta revisión documental</p>
              )}
              {uploading && <FileUploadPlaceholder isImage={this.state.uploading_is_image} />}
            </div>

            <div style={{ padding: '20px' }}>
              {this.props.writeComments && (
                <div className='container-textarea'>
                  <div className='container-white'>
                    <textarea
                      className='form-control'
                      ref={(ref) => (this.textareaRef = ref)}
                      rows={1}
                      value={this.state.form.message}
                      placeholder='Escribir'
                      name='message'
                      style={{ minHeight: 38, resize: 'none' }}
                      onKeyDown={(e: any) => this.autoGrow(e)}
                      onChange={this.change}
                    />
                    <PaperClip
                      formats={[
                        'pdf',
                        'doc',
                        'docx',
                        'xls',
                        'xlsx',
                        'ppt',
                        'pptx',
                        'png',
                        'jpg',
                        'jpeg',
                        'webp',
                        'bmp',
                        'tiff',
                        'tif',
                        'avif',
                        'heic',
                        'heif',
                      ]}
                      successText='Subir documento'
                      pendingText='Subir documento'
                      value={''}
                      onChange={(e: any) => {
                        this.setState({
                          uploading_is_image: e.target.value?.type === 'image/png',
                        });
                        this.send(e.target.value);
                      }}
                    />
                  </div>
                  <div>
                    <button className='btn' onClick={() => this.send()}>
                      <img src={SendIcon} className='send-icon' />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(ModalComments);
